import { useLocation } from '@reach/router';
import { type PropsWithChildren, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import type { InteractionTrackingEvent } from '@xing-com/crate-communication-tracking';
import { useTracking } from '@xing-com/crate-communication-tracking';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import {
  useSearchAlertRequiredFields,
  useSearchAlerts,
  useShowJobPreferencesSearchAlertBanner,
  useToggleSearchAlert,
  useUpdateJobPreferencesUserFlag,
} from '@xing-com/crate-jobs-hooks';
import {
  getNwtCleanQuery,
  useJobsNwtTracking,
} from '@xing-com/crate-jobs-tracking';
import type { SearchAlertsJobPreferencesBannerProps } from '@xing-com/crate-jobs-xinglets';
import { BodyCopy } from '@xing-com/typography';

import { SEARCH_ALERT_JOB_PREFERENCES_LABEL_ID } from '../constants';

import { SearchAlertBannerAction } from './job-preferences-search-alert-banner-action';
import * as S from './job-preferences-search-alert-banner.styles';

type PropTrackActionData = {
  PropActionOrigin?: string | undefined;
  PropTrackAction?: string | undefined;
};

const Banner: React.FC<
  PropsWithChildren<SearchAlertsJobPreferencesBannerProps>
> = ({ children, itemCount, trackingToken }) => {
  const { isLoggedIn } = useLoginState();
  const { loading: isLoading } = useSearchAlerts();
  const { keywords, location: searchLocation } = useSearchAlertRequiredFields();
  const { data, loading: isShowJobPreferencesSearchAlertBannerLoading } =
    useShowJobPreferencesSearchAlertBanner();
  const updateJobPreferencesUserFlag = useUpdateJobPreferencesUserFlag();

  const { isAlertActive } = useToggleSearchAlert();
  const { track: trackAdobeEvent } =
    useTracking<InteractionTrackingEvent<PropTrackActionData>>();
  const trackNwt = useJobsNwtTracking();
  const location = useLocation();

  const [isClosed, setIsClosed] = useState(false);

  const filledPreferences =
    data?.viewer?.jobPreferencesDataStateInfo?.filledPreferences || 0;
  const isBannerHidden =
    data?.viewer?.jobPreferenceUserFlags?.serpBannerHidden || false;

  const onBannerClose = (): void => {
    const query = new URLSearchParams(location.search);
    setIsClosed(true);
    trackAdobeEvent({
      type: 'interaction',
      event: 'PropTrackAction',
      PropActionOrigin: `jobs_toggel_${isAlertActive ? 'on' : 'off'}`,
      PropTrackAction: 'jobs_search_alert_banner_close',
    });
    trackNwt({
      element: `jobs_preferences_search_alert_${
        isAlertActive ? 'on' : 'off'
      }_banner_close`,
      event: 'clicked',
      eventSchema: 'searchresult',
      itemCount: itemCount,
      page: 'jobs_search/serp',
      trackingToken: trackingToken,
      query: getNwtCleanQuery(query),
    });
    updateJobPreferencesUserFlag({ serpBannerHidden: true });
  };

  const isShown =
    isLoggedIn &&
    !isLoading &&
    !isShowJobPreferencesSearchAlertBannerLoading &&
    !!(keywords || searchLocation) &&
    !isClosed &&
    filledPreferences === 0 &&
    !isBannerHidden;

  return (
    <S.Banner
      data-testid="job-preferences-search-alert-banner"
      display="fixed"
      handleOnClose={onBannerClose}
      show={isShown}
      showCloseButton
    >
      {children}
    </S.Banner>
  );
};

export const JobPreferencesSearchAlertBanner: React.FC<
  SearchAlertsJobPreferencesBannerProps
> = ({ itemCount, trackingToken }) => {
  return (
    <Banner itemCount={itemCount} trackingToken={trackingToken}>
      <SearchAlertBannerAction
        itemCount={itemCount}
        trackingToken={trackingToken}
      />
      <BodyCopy
        lineHeight="dense"
        as="label"
        // @ts-expect-error FIXME: SC6
        htmlFor={SEARCH_ALERT_JOB_PREFERENCES_LABEL_ID}
        noMargin
      >
        <FormattedMessage id="JOB_PREFERENCES_SEARCH_ALERT_BANNER_TEXT" />
      </BodyCopy>
    </Banner>
  );
};
